import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, clearToken, getToken, setToken } from './api';
import type { Kachel, Rolle, User } from './types';

interface AuthState {
  user: User | null;
  kacheln: Kachel[];
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  can: (...rollen: Rolle[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [kacheln, setKacheln] = useState<Kachel[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      setReady(true);
      return;
    }
    api<{ user: User; kacheln: Kachel[] }>('/api/auth/me')
      .then((r) => {
        setUser(r.user);
        setKacheln(r.kacheln);
      })
      .catch(() => clearToken())
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api<{ token: string; user: User; kacheln: Kachel[] }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    setToken(r.token);
    setUser(r.user);
    setKacheln(r.kacheln);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    window.location.href = '/login';
  }, []);

  const can = useCallback((...rollen: Rolle[]) => (user ? rollen.includes(user.rolle) : false), [user]);

  const value = useMemo(() => ({ user, kacheln, ready, login, logout, can }), [user, kacheln, ready, login, logout, can]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth ausserhalb von AuthProvider');
  return context;
}

export const ROLLEN_LABEL: Record<Rolle, string> = {
  admin: 'Admin',
  verkauf: 'Verkauf / Laden',
  fahrer: 'Fahrer / Monteur',
  techniker: 'Werkstatt-Techniker',
};
